/**
 * RackSandbox — montar equipos en un armario de mentira.
 *
 * Enseña lo único que no se puede explicar con texto: que un equipo ocupa N
 * unidades, que no se solapa con otro y que si no cabe, no cabe. Sin API.
 */

import React, { useMemo, useState } from 'react';

const UNITS = 10;

const DEVICES = [
  { id: 'srv', name: 'Servidor 2U', u: 2, tone: 'blue' },
  { id: 'sw', name: 'Switch 1U', u: 1, tone: 'green' },
  { id: 'pdu', name: 'PDU 1U', u: 1, tone: 'amber' },
  { id: 'sto', name: 'Almacenamiento 3U', u: 3, tone: 'violet' },
];

export default function RackSandbox() {
  const [selected, setSelected] = useState('srv');
  const [placements, setPlacements] = useState([]);
  const [error, setError] = useState('');

  const device = DEVICES.find((item) => item.id === selected);

  const occupancy = useMemo(() => {
    const map = new Array(UNITS).fill(null);
    placements.forEach((placement) => {
      for (let offset = 0; offset < placement.u; offset += 1) {
        map[placement.slot + offset] = placement;
      }
    });
    return map;
  }, [placements]);

  const usedUnits = occupancy.filter(Boolean).length;

  const place = (slot) => {
    setError('');
    if (!device) return;
    const top = slot + device.u - 1;
    if (top >= UNITS) {
      setError(`${device.name} no cabe empezando en U${slot + 1}: se sale del armario.`);
      return;
    }
    for (let cursor = slot; cursor <= top; cursor += 1) {
      if (occupancy[cursor]) {
        setError(`U${cursor + 1} ya está ocupada por ${occupancy[cursor].name}.`);
        return;
      }
    }
    setPlacements((prev) => [...prev, { ...device, slot, id: `${device.id}-${Date.now()}` }]);
  };

  const remove = (placementId) => {
    setPlacements((prev) => prev.filter((placement) => placement.id !== placementId));
    setError('');
  };

  return (
    <div className="acr-frame">
      <div className="acr-palette">
        <span className="acr-palette-title">Equipos</span>
        {DEVICES.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`acr-device acr-device--${item.tone}${selected === item.id ? ' is-selected' : ''}`}
            onClick={() => setSelected(item.id)}
          >
            <span className="acr-device-name">{item.name}</span>
            <span className="acr-device-u">{item.u}U</span>
          </button>
        ))}
        <p className="acr-palette-hint">
          Elige un equipo y pulsa una unidad libre del armario. Vuelve a pulsar un equipo montado para
          desmontarlo.
        </p>
      </div>

      <div className="acr-rack" role="grid" aria-label="Armario de prácticas">
        {Array.from({ length: UNITS }).map((_, index) => {
          const slot = UNITS - 1 - index; // se pinta de arriba (U10) a abajo (U1)
          const placement = occupancy[slot];
          return (
            <button
              key={slot}
              type="button"
              role="gridcell"
              className={`acr-unit${placement ? ` acr-unit--filled acr-unit--${placement.tone}` : ''}`}
              onClick={() => (placement ? remove(placement.id) : place(slot))}
              title={placement ? `Desmontar ${placement.name}` : `Montar ${device?.name} en U${slot + 1}`}
            >
              <span className="acr-unit-label">U{slot + 1}</span>
              <span className="acr-unit-content">{placement ? placement.name : ''}</span>
            </button>
          );
        })}
      </div>

      <div className="acr-status">
        <div className="acr-counts">
          <span><strong>{usedUnits}</strong> U ocupadas</span>
          <span><strong>{UNITS - usedUnits}</strong> U libres</span>
          <span><strong>{placements.length}</strong> equipos</span>
        </div>
        {error && <div className="acr-error" role="alert">{error}</div>}
        {placements.length > 0 && (
          <button type="button" className="acr-reset" onClick={() => { setPlacements([]); setError(''); }}>
            Vaciar el armario
          </button>
        )}
      </div>
    </div>
  );
}
